import { Box, Card, CardContent, Typography, Chip } from "@mui/material";
import { GRADE_COLORS, scoreColor } from "./seoColors";

const CONTENT_TYPE_LABELS = {
  news:          "News",
  article:       "Article",
  howto:         "How-To",
  entertainment: "Entertainment",
};

// ── Gradient SVG ring ────────────────────────────────────────────────────────

function ScoreRing({ score, size = 80, strokeWidth = 8, gradientId, gradStart, gradEnd }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (score / 100) * circumference;
  const useGrad = !!(gradientId && gradStart && gradEnd);
  const textColor = useGrad ? gradEnd : scoreColor(score);
  const strokeColor = useGrad ? `url(#${gradientId})` : scoreColor(score);

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} overflow="visible">
        {useGrad && (
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={gradStart} />
              <stop offset="100%" stopColor={gradEnd} />
            </linearGradient>
          </defs>
        )}
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="currentColor" strokeWidth={strokeWidth}
          style={{ color: "rgba(128,128,128,0.12)" }}
        />
        {/* Progress */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={strokeColor} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{
            transition: "stroke-dashoffset 0.9s ease",
            filter: useGrad ? `drop-shadow(0 0 5px ${gradEnd}60)` : "none",
          }}
        />
      </svg>
      <Typography
        sx={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800,
          fontSize: size >= 100 ? "1.6rem" : size >= 80 ? "1.05rem" : "0.9rem",
          color: textColor,
        }}
      >
        {score}
      </Typography>
    </Box>
  );
}

// ── Sub-score card ───────────────────────────────────────────────────────────

function ScoreCard({ label, score, category, activeCategory, onCategoryClick }) {
  const isActive = activeCategory === category;

  return (
    <Card
      onClick={() => onCategoryClick?.(category)}
      sx={{
        flex: 1, minWidth: 130, maxWidth: 210,
        cursor: "pointer",
        border: "1px solid",
        borderColor: isActive ? "text.primary" : "divider",
        boxShadow: "none",
        transition: "border-color 0.2s ease",
        "&:hover": { borderColor: "text.secondary" },
      }}
    >
      <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: "20px !important" }}>
        <ScoreRing score={score} size={82} />
        <Typography
          variant="caption"
          fontWeight={isActive ? 700 : 500}
          textAlign="center"
          sx={{ color: isActive ? "text.primary" : "text.secondary", lineHeight: 1.35 }}
        >
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

export default function SeoScores({ report, text, activeCategory, onCategoryClick }) {
  const grade = report.grade || "?";
  const gradeColor = GRADE_COLORS[grade] || "#757575";
  const tags = (report.tags || []).filter(t => !t.includes("top-performer") || report.overall_score >= 70);
  const strengths = report.strengths || [];
  const contentTypeLabel = CONTENT_TYPE_LABELS[report.content_type] || report.content_type;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

      {/* ── Verdict card ──────────────────────────────────────────────── */}
      <Card
        sx={{
          border: "1px solid",
          borderColor: "divider",
        }}
      >

        <CardContent sx={{ p: 3, pt: "22px !important" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, flexWrap: "wrap" }}>

            {/* Score + grade */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
              <ScoreRing
                score={report.overall_score} size={112} strokeWidth={10}
              />
              <Box sx={{
                px: 2, py: 0.4, borderRadius: "16px",
                background: `${gradeColor}1a`,
                border: `1px solid ${gradeColor}50`,
              }}>
                <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: gradeColor, letterSpacing: "0.08em" }}>
                  {grade}
                </Typography>
              </Box>
            </Box>

            {/* Details */}
            <Box sx={{ flex: 1, minWidth: 180 }}>

              {/* Content type + page title */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
                {contentTypeLabel && (
                  <Chip
                    label={contentTypeLabel}
                    size="small"
                    sx={{
                      height: 20, fontSize: 10, fontWeight: 700,
                      bgcolor: "action.selected",
                      color: "text.primary",
                      borderRadius: "10px",
                    }}
                  />
                )}
                {report.page_title && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontStyle: "italic", lineHeight: 1.4 }}
                  >
                    {report.page_title}
                  </Typography>
                )}
              </Box>

              {/* Overline label */}
              <Typography variant="overline" sx={{ color: "text.disabled", letterSpacing: "0.15em", fontSize: 10 }}>
                {text.overallScore}
              </Typography>

              {/* AI verdict — the star of the show */}
              {report.score_summary && (
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ lineHeight: 1.5, mt: 0.5, mb: 1.5, color: "text.primary" }}
                >
                  {report.score_summary}
                </Typography>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {tags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 600,
                        bgcolor: "background.secondary",
                        color: "text.secondary",
                        borderRadius: "8px",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── Strengths strip ───────────────────────────────────────────── */}
      {strengths.length > 0 && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
          px: 2, py: 1, borderRadius: 2,
          bgcolor: "action.hover",
          border: "1px solid", borderColor: "divider",
        }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ flexShrink: 0 }}>
            Strengths:
          </Typography>
          {strengths.slice(0, 6).map((s, i) => (
            <Chip
              key={i} label={s} size="small"
              sx={{
                height: 20, fontSize: 10, fontWeight: 500,
                bgcolor: "background.paper",
                color: "text.secondary",
                borderRadius: "8px",
              }}
            />
          ))}
        </Box>
      )}

      {/* ── Filter hint ───────────────────────────────────────────────── */}
      <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
        {text.filterHint || "Click a pillar to filter findings"}
      </Typography>

      {/* ── Sub-score cards ───────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <ScoreCard
          label={text.seoScore} score={report.seo_score}
          category="SEO Technical"
          activeCategory={activeCategory} onCategoryClick={onCategoryClick}
        />
        <ScoreCard
          label={text.contentScore} score={report.content_score}
          category="Content"
          activeCategory={activeCategory} onCategoryClick={onCategoryClick}
        />
        <ScoreCard
          label={text.geoScore} score={report.geo_score}
          category="GEO"
          activeCategory={activeCategory} onCategoryClick={onCategoryClick}
        />
      </Box>
    </Box>
  );
}
